import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import {
  FileText,
  Search,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Hash,
  Check,
  X
} from 'lucide-react';

const SurveyorExpenses = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [expenseDate, setExpenseDate] = useState('');
  const [ordering, setOrdering] = useState('-expense_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [summary, setSummary] = useState(null);

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [viewMode, setViewMode] = useState('all'); // all | my | pending

  const [createForm, setCreateForm] = useState({
    expense_type: 'meal',
    custom_expense_type: '',
    amount: '',
    description: '',
    bill_image: null,
    expense_date: '',
    status: 'draft',
    notes: ''
  });

  const [editForm, setEditForm] = useState({
    expense_type: 'meal',
    custom_expense_type: '',
    amount: '',
    description: '',
    bill_image: null,
    expense_date: '',
    status: 'draft',
    notes: ''
  });

  const expenseTypes = [
    'meal',
    'travel',
    'office_supplies',
    'utilities',
    'maintenance',
    'consulting',
    'advertising',
    'transportation',
    'accommodation',
    'other'
  ];

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = (isMultipart = false) => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toMoney2 = (value, defaultValue = '0.00') => {
    const num = Number(value);
    if (Number.isNaN(num)) return defaultValue;
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  const buildFormData = (form) => {
    const fd = new FormData();
    fd.append('expense_type', form.expense_type);
    if (form.custom_expense_type) fd.append('custom_expense_type', form.custom_expense_type);
    fd.append('amount', toMoney2(form.amount));
    fd.append('description', form.description || '');
    fd.append('expense_date', form.expense_date || '');
    fd.append('status', form.status || 'draft');
    if (form.notes) fd.append('notes', form.notes);
    if (form.bill_image instanceof File) {
      fd.append('bill_image', form.bill_image);
    }
    return fd;
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.EXPENSES_SUMMARY, { headers: getAuthHeaders() });
      setSummary(res.data);
    } catch {
      // ignore summary fetch errors
    }
  };

  const fetchItems = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (viewMode === 'pending') {
        const res = await axios.get(API_ENDPOINTS.EXPENSES_PENDING, { headers: getAuthHeaders() });
        const data = res.data || [];
        setItems(data);
        setTotalCount(Array.isArray(data) ? data.length : data.count || 0);
        setNextPage(null);
        setPrevPage(null);
        setCurrentPage(1);
        return;
      }

      if (viewMode === 'my') {
        const res = await axios.get(API_ENDPOINTS.EXPENSES_MY, { headers: getAuthHeaders() });
        const data = res.data || [];
        setItems(data);
        setTotalCount(Array.isArray(data) ? data.length : data.count || 0);
        setNextPage(null);
        setPrevPage(null);
        setCurrentPage(1);
        return;
      }

      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('expense_type', filterType);
      if (expenseDate) params.append('expense_date', expenseDate);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-expense_date');

      const response = await axios.get(`${API_ENDPOINTS.EXPENSES_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch expenses'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm((p) => ({ ...p, expense_date: today }));
    setEditForm((p) => ({ ...p, expense_date: today }));
    fetchSummary();
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType, ordering, expenseDate, viewMode]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      expense_type: 'meal',
      custom_expense_type: '',
      amount: '',
      description: '',
      bill_image: null,
      expense_date: today,
      status: 'draft',
      notes: ''
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files?.[0];
    setter((prev) => ({ ...prev, bill_image: file || null }));
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.amount) {
      setError('Please enter amount');
      return;
    }
    const amountMoney = toMoney2(createForm.amount);
    if (exceedsMaxDigits(amountMoney)) {
      setError('Amount must not exceed 15 total digits.');
      return;
    }
    setIsCreateLoading(true);
    try {
      const fd = buildFormData(createForm);
      const response = await axios.post(API_ENDPOINTS.EXPENSES_CREATE, fd, {
        headers: getAuthHeaders(true)
      });
      const msg = response?.data?.message || 'Expense created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchSummary();
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create expense'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_ENDPOINTS.EXPENSES_DETAIL(row.id), { headers: getAuthHeaders() });
      setSelected(response.data);
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch expense details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      const response = await axios.get(API_ENDPOINTS.EXPENSES_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);
      setEditForm({
        expense_type: data.expense_type || 'meal',
        custom_expense_type: data.custom_expense_type || '',
        amount: data.amount || '',
        description: data.description || '',
        bill_image: null,
        expense_date: data.expense_date ? data.expense_date.split('T')[0] : '',
        status: data.status || 'draft',
        notes: data.notes || ''
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load expense for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!editForm.amount) {
      setError('Please enter amount');
      return;
    }
    const amountMoney = toMoney2(editForm.amount);
    if (exceedsMaxDigits(amountMoney)) {
      setError('Amount must not exceed 15 total digits.');
      return;
    }
    setIsEditLoading(true);
    try {
      const fd = buildFormData(editForm);
      const response = await axios.put(API_ENDPOINTS.EXPENSES_UPDATE(selected.id), fd, {
        headers: getAuthHeaders(true)
      });
      const msg = response?.data?.message || 'Expense updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchSummary();
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update expense'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.EXPENSES_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Expense deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchSummary();
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete expense'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const doAction = async (id, endpoint, successFallback, payload = {}) => {
    setIsActionLoading(true);
    try {
      const response = await axios.post(endpoint(id), payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || response?.data?.status || response?.data?.success || successFallback;
      setSuccessMessage(msg);
      fetchSummary();
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSubmitForApproval = (row) =>
    doAction(row.id, API_ENDPOINTS.EXPENSES_SUBMIT, 'Submitted for approval');
  const handleApprove = (row) =>
    doAction(row.id, API_ENDPOINTS.EXPENSES_APPROVE, 'Expense approved');
  const handleReject = (row) => {
    const reason = window.prompt('Enter rejection reason');
    if (!reason) return;
    doAction(row.id, API_ENDPOINTS.EXPENSES_REJECT, 'Expense rejected', { reason });
  };
  const handleMarkPaid = (row) =>
    doAction(row.id, API_ENDPOINTS.EXPENSES_MARK_PAID, 'Expense marked as paid');

  const filteredItems = items;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
              <p className="text-2xl font-bold text-foreground">{summary?.total_expenses ?? totalCount}</p>
              <p className="text-xs text-muted-foreground">Amount: {formatCurrency(summary?.total_amount)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-foreground">
                {summary?.by_status?.draft?.count ?? items.filter((q) => q.status === 'draft').length}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(summary?.by_status?.draft?.total)}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Submitted</p>
              <p className="text-2xl font-bold text-foreground">
                {summary?.by_status?.submitted?.count ?? items.filter((q) => q.status === 'submitted').length}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(summary?.by_status?.submitted?.total)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Check className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Approved/Paid</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'approved' || q.status === 'paid').length}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(
                  (summary?.by_status?.approved?.total || 0) + (summary?.by_status?.paid?.total || 0)
                )}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <input
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent capitalize"
          >
            <option value="all">All Types</option>
            {expenseTypes.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-expense_date">Date: Newest first</option>
            <option value="expense_date">Date: Oldest first</option>
            <option value="-amount">Amount: High to Low</option>
            <option value="amount">Amount: Low to High</option>
          </select>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All</option>
            <option value="my">My Expenses</option>
            <option value="pending">Pending Approval</option>
          </select>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Expense
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading expenses...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No expenses found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {row.description || row.display_category || 'N/A'}
                            </div>
                            <div className="text-xs text-muted-foreground">{row.notes || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(row.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.expense_date ? new Date(row.expense_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm capitalize text-gray-900">
                        {row.expense_type_display || row.expense_type || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <span className="capitalize">{row.status_display || row.status || 'N/A'}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.created_by_username || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(row)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(row)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSubmitForApproval(row)}
                            title="Submit for Approval"
                            disabled={isActionLoading || row.status !== 'draft'}
                          >
                            <Hash className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(row)}
                            title="Approve"
                            disabled={isActionLoading || row.status !== 'submitted'}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReject(row)}
                            title="Reject"
                            disabled={isActionLoading || row.status !== 'submitted'}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (row.status !== 'approved') {
                                setError('Can only mark as paid after approval.');
                                return;
                              }
                              handleMarkPaid(row);
                            }}
                            title="Mark Paid"
                            disabled={isActionLoading || row.status !== 'approved'}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(row)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Expense Details</h3>
                <p className="text-sm text-muted-foreground">{selected.expense_type_display || selected.expense_type || 'Expense'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-muted-foreground">Amount</p><p className="font-semibold">{formatCurrency(selected.amount)}</p></div>
                <div><p className="text-muted-foreground">Date</p><p className="font-semibold">{selected.expense_date ? new Date(selected.expense_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Status</p><p className="font-semibold capitalize">{selected.status_display || selected.status || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Type</p><p className="font-semibold capitalize">{selected.expense_type_display || selected.expense_type || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Created By</p><p className="font-semibold">{selected.created_by_username || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Approved By</p><p className="font-semibold">{selected.approved_by_username || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Approved At</p><p className="font-semibold">{selected.approved_at ? new Date(selected.approved_at).toLocaleString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Paid At</p><p className="font-semibold">{selected.paid_at ? new Date(selected.paid_at).toLocaleString() : 'N/A'}</p></div>
              </div>

              {selected.description && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Description</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.description}</p>
                </div>
              )}
              {selected.notes && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Notes</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.notes}</p>
                </div>
              )}
              {selected.rejection_reason && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Rejection Reason</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.rejection_reason}</p>
                </div>
              )}
              {selected.bill_image_url && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Bill Image</p>
                  <a className="text-primary underline" href={selected.bill_image_url} target="_blank" rel="noopener noreferrer">
                    View Receipt
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Expense</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type</label>
                  <select
                    value={createForm.expense_type}
                    onChange={(e) => setCreateForm((p) => ({ ...p, expense_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary capitalize"
                  >
                    {expenseTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Expense Type</label>
                  <input
                    type="text"
                    value={createForm.custom_expense_type}
                    onChange={(e) => setCreateForm((p) => ({ ...p, custom_expense_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.amount}
                    onChange={(e) => setCreateForm((p) => ({ ...p, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date</label>
                  <input
                    type="date"
                    value={createForm.expense_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, expense_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange(setCreateForm)}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreateLoading}>
                  {isCreateLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Edit Expense</h3>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type</label>
                  <select
                    value={editForm.expense_type}
                    onChange={(e) => setEditForm((p) => ({ ...p, expense_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary capitalize"
                  >
                    {expenseTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Expense Type</label>
                  <input
                    type="text"
                    value={editForm.custom_expense_type}
                    onChange={(e) => setEditForm((p) => ({ ...p, custom_expense_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date</label>
                  <input
                    type="date"
                    value={editForm.expense_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, expense_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange(setEditForm)}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isEditLoading}>
                  {isEditLoading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Expense</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete expense {deleteTarget.description || deleteTarget.id}?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyorExpenses;


